import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  UserPlus, 
  Send,
  Flame,
  Heart,
  MessageCircle,
  Check,
  X,
  Clock,
  Copy,
  Mail,
  Sparkles,
  Trophy
} from "lucide-react";

// Partner status badge
function PartnerStatusBadge({ status }) {
  const statusConfig = {
    pending: { label: "Pending", color: "bg-yellow-500/10 text-yellow-600", icon: Clock },
    active: { label: "Active", color: "bg-green-500/10 text-green-600", icon: Check },
    declined: { label: "Declined", color: "bg-red-500/10 text-red-600", icon: X }
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Single partner card
export function PartnerCard({ partner, onSendMessage, onRemove, isIncoming = false, onAccept, onDecline }) {
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await onSendMessage(partner.partnership_id, message.trim());
      setMessage("");
      setShowMessageDialog(false);
      toast.success("Encouragement sent!");
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const isActive = partner.status === "active";
  const isPending = partner.status === "pending";

  return (
    <Card className="glass-card overflow-hidden" data-testid={`partner-card-${partner.partnership_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#d4e4d4] flex items-center justify-center">
              <Users className="w-6 h-6 text-[#5a8a5a]" />
            </div>
            <div>
              <p className="font-medium text-[#a69373]">{partner.partner_name || "Partner"}</p>
              <PartnerStatusBadge status={partner.status} />
            </div>
          </div>
          
          {isActive && (
            <div className="flex items-center gap-1 text-orange-500">
              <Flame className="w-4 h-4" />
              <span className="text-sm font-medium">{partner.partner_streak || 0}</span>
            </div>
          )}
        </div>

        {/* Partner's recent activity */}
        {isActive && partner.last_checkin && (
          <div className="bg-[#f5f9f5] rounded-lg p-3 mb-3">
            <p className="text-xs text-[#a69373]/50 mb-1">Last check-in</p>
            <p className="text-sm text-[#a69373]">
              {new Date(partner.last_checkin).toLocaleDateString()} at{" "}
              {new Date(partner.last_checkin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {isIncoming && isPending ? (
            <>
              <Button 
                size="sm" 
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => onAccept(partner.partnership_id)}
              >
                <Check className="w-4 h-4 mr-1" />
                Accept
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1"
                onClick={() => onDecline(partner.partnership_id)}
              >
                <X className="w-4 h-4 mr-1" />
                Decline
              </Button>
            </>
          ) : isActive ? (
            <>
              <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="flex-1 btn-primary">
                    <Heart className="w-4 h-4 mr-1" />
                    Send Encouragement
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-[#a69373]">Send Encouragement</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-[#a69373]/60">
                      Send a message to encourage {partner.partner_name || "your partner"} on their journey.
                    </p>
                    <Textarea
                      placeholder="You're doing great! Keep going..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="min-h-[100px]"
                      maxLength={200}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#a69373]/40">{message.length}/200</span>
                      <Button onClick={handleSendMessage} disabled={sending || !message.trim()}>
                        {sending ? "Sending..." : "Send"}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onRemove(partner.partnership_id)}
              >
                Remove
              </Button>
            </>
          ) : isPending && !isIncoming ? (
            <Button size="sm" variant="outline" className="w-full" disabled>
              <Clock className="w-4 h-4 mr-1" />
              Waiting for response
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// Invite partner dialog
export function InvitePartnerDialog({ onInvite }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const { token } = useAuth();

  const generateInviteLink = async () => {
    try {
      const res = await axios.post(
        `${API}/partners/invite-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteLink(res.data.invite_link);
    } catch (err) {
      toast.error("Failed to generate invite link");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleEmailInvite = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    
    setSending(true);
    try {
      await onInvite(email.trim());
      setEmail("");
      setOpen(false);
      toast.success("Invitation sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (open && !inviteLink) {
      generateInviteLink();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary w-full" data-testid="invite-partner-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Accountability Partner
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#a69373]">Invite a Partner</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Share link option */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-[#a69373]">Share invite link</p>
            <div className="flex gap-2">
              <Input 
                value={inviteLink} 
                readOnly 
                className="flex-1 text-sm"
                placeholder="Generating link..."
              />
              <Button 
                variant="outline" 
                onClick={handleCopyLink}
                disabled={!inviteLink}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-[#a69373]/40">or</span>
            </div>
          </div>

          {/* Email invite option */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-[#a69373]">Send email invitation</p>
            <div className="flex gap-2">
              <Input 
                type="email"
                placeholder="friend@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleEmailInvite} disabled={sending}>
                {sending ? "Sending..." : <Mail className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <p className="text-xs text-[#a69373]/50 text-center">
            Your partner will be able to see your streak and send you encouragement.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Messages from partner
export function PartnerMessages({ messages, onDismiss }) {
  if (!messages || messages.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="partner-messages">
      {messages.map((msg) => (
        <Card key={msg.message_id} className="glass-card border-[#d4e4d4] animate-fade-in">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-pink-500/10 flex items-center justify-center shrink-0">
                <Heart className="w-4 h-4 text-pink-500" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#a69373]/50 mb-1">
                  From {msg.sender_name || "Your partner"}
                </p>
                <p className="text-sm text-[#a69373]">{msg.message}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0"
                onClick={() => onDismiss(msg.message_id)}
              >
                <X className="w-4 h-4 text-[#a69373]/30" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Main Accountability Partners component
export function AccountabilityPartners() {
  const { token } = useAuth();
  const [partners, setPartners] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPartners = async () => {
    try {
      const [partnersRes, messagesRes] = await Promise.all([
        axios.get(`${API}/partners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/partners/messages`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Separate active partners and pending incoming requests
      const allPartners = partnersRes.data.partners || [];
      setPartners(allPartners.filter(p => p.status === "active" || (p.status === "pending" && !p.is_incoming)));
      setPendingRequests(allPartners.filter(p => p.status === "pending" && p.is_incoming));
      setMessages(messagesRes.data || []);
    } catch (err) {
      console.error("Failed to fetch partners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleInvite = async (email) => {
    await axios.post(
      `${API}/partners/invite`,
      { email },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchPartners();
  };

  const handleAccept = async (partnershipId) => {
    try {
      await axios.post(
        `${API}/partners/${partnershipId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Partnership accepted!");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to accept");
    }
  };

  const handleDecline = async (partnershipId) => {
    try {
      await axios.post(
        `${API}/partners/${partnershipId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Partnership declined");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to decline");
    }
  };

  const handleRemove = async (partnershipId) => {
    if (!window.confirm("Remove this accountability partner?")) return;
    
    try {
      await axios.delete(
        `${API}/partners/${partnershipId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Partner removed");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to remove partner");
    }
  };

  const handleSendMessage = async (partnershipId, message) => {
    await axios.post(
      `${API}/partners/${partnershipId}/message`,
      { message },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleDismissMessage = async (messageId) => {
    try {
      await axios.post(
        `${API}/partners/messages/${messageId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
    } catch (err) {
      console.error("Failed to dismiss message:", err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card className="glass-card animate-pulse">
          <CardContent className="p-4">
            <div className="h-20 bg-gray-200 rounded" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="accountability-partners">
      {/* Encouragement messages */}
      <PartnerMessages messages={messages} onDismiss={handleDismissMessage} />

      {/* Pending incoming requests */}
      {pendingRequests.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-[#a69373] px-1">Partner Requests</p>
          {pendingRequests.map(partner => (
            <PartnerCard
              key={partner.partnership_id}
              partner={partner}
              isIncoming={true}
              onAccept={handleAccept}
              onDecline={handleDecline}
              onSendMessage={handleSendMessage}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      {/* Active partners and outgoing pending */}
      {partners.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-[#a69373] px-1">Your Partners</p>
          {partners.map(partner => (
            <PartnerCard
              key={partner.partnership_id}
              partner={partner}
              onSendMessage={handleSendMessage}
              onRemove={handleRemove}
            />
          ))}
        </div>
      ) : pendingRequests.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-[#d4e4d4] mx-auto mb-4" />
            <h3 className="text-[#a69373] font-heading text-lg mb-2">No Partners Yet</h3>
            <p className="text-[#a69373]/50 text-sm mb-4">
              Invite a friend to be your accountability partner and support each other's journey.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Invite button */}
      <InvitePartnerDialog onInvite={handleInvite} />
    </div>
  );
}

export default AccountabilityPartners;
